/*
 * DashboardPage - convenient component for creating our standard sweetprocess dashboard
 * */
import React, {type ReactNode, useEffect, useMemo, useState} from 'react';

import {DashboardChatWidget} from 'app/chat-app/components/DashboardChatWidget';
import {LinkPagination} from 'app/components/general/LinkPagination';
import MainCol from 'app/components/general/MainCol';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {StyledSidebarDIV} from 'app/components/general/Sidebar';
import {DashboardLoadingContent} from 'app/documents/components/DocumentsDashboard/DashboardLoadingContent';
import {applyFilter} from 'app/general/dashboards/DashboardFilterbar';
import {DashboardItemsCounter} from 'app/general/dashboards/DashboardItemsCounter';
import {BootstrapMinMediumMediaQuery} from 'app/general/mediaqueries';
import {SPPage, type SPPageProps} from 'app/general/structures/SPPage';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {DocumentsNotificationsProvider} from 'app/notifly/components/DocumentsNotificationsProvider';
import {PresenceSubscriber} from 'app/presence/components/PresenceSubscriber';
import {
  useModelChangeSubscription,
  type WSModelChangeMessageHandler,
} from 'app/subscriptions';
import {convertListResponseToStoredResponse} from 'app/tasks/components/TasksDashboard/AllTasksList/TasksLinkPagination';
import {gt} from 'app/utils/i18n';

import type {NotificationType} from 'app/notifly/types';
import type {UseListQueryType} from 'app/store/sweet-api/api-query';
import type {Location} from 'history';
import type {InjectedRouter} from 'react-router';
import type {ContentType} from 'sweet-types';

interface Props<
  ItemType extends {id: number | string; url?: string},
  DashboardParams,
  FetchParams extends {page: number | string}
> extends Pick<
    SPPageProps,
    | 'breadcrumbProps'
    | 'title'
    | 'pageTitle'
    | 'subTitle'
    | 'pageActions'
    | 'actionArea'
    | 'location'
    | 'router'
  > {
  useDataHook: UseListQueryType<FetchParams, ItemType>;
  hookOptions?: {refetchOnMountOrArgChange?: boolean | number};
  useFiltersHook: (
    location: Location<FetchParams>,
    router: InjectedRouter
  ) => {
    filters: DashboardParams;
    params: FetchParams;
    viewingAll: boolean;
    resetFilters: () => void;
  };
  filterbar?: (props: DashboardPassedProps<FetchParams>) => ReactNode;
  renderItem: (
    item: ItemType,
    props: DashboardPassedProps<FetchParams>,
    isSelected: boolean,
    addToSelection: (documentToAdd) => unknown,
    removeFromSelection: (documentToRemove) => unknown,
    notifications?: Array<NotificationType>
  ) => JSX.Element;
  contentType?: ContentType;
  enableNotifications?: boolean;
  enableBulkSelect?: boolean;
  onModalChangeMessage?: (
    props: DashboardPassedProps<FetchParams>
  ) => WSModelChangeMessageHandler;
  messages: {
    counter?: (count: number) => ReactNode;
    topListActions?: (
      selectedDocuments: Array<ItemType>,
      props: DashboardPassedProps<FetchParams>
    ) => ReactNode;
    notFound: (
      props: DashboardPassedProps<FetchParams>,
      MessageElement: typeof NoResultsMessage
    ) => ReactNode;
  };
  subscribeToPresence?: {
    contentType: ContentType;
    objectsIds: (objects: Array<ItemType>) => Array<string | number>;
  };
  sidebar?: ReactNode;
  header?: (props: DashboardPassedProps<FetchParams>) => ReactNode;
  footer?: (props: DashboardPassedProps<FetchParams>) => ReactNode;
}

export interface DashboardPassedProps<
  FetchParams extends {page: number | string}
> {
  resetFilters: () => void;
  viewingAll: boolean;
  count: number | null;
  params: FetchParams;
  refetch: () => void;
}

export function DashboardPage<
  ItemType extends {id: number | string; url?: string},
  DashboardParams extends Record<string, unknown>,
  FetchParams extends {page: number | string}
>({
  location,
  useDataHook,
  useFiltersHook,
  hookOptions = {},
  router,
  filterbar,
  renderItem,
  onModalChangeMessage,
  messages,
  subscribeToPresence,
  contentType,
  enableNotifications = false,
  sidebar,
  header,
  footer,
  enableBulkSelect = false,
  ...pageProps
}: Props<ItemType, DashboardParams, FetchParams>) {
  const {filters, params, viewingAll, resetFilters} = useFiltersHook(
    location,
    router
  );

  const {
    data: response,
    isFetching,
    refetch,
    currentData,
  } = useDataHook(params, {...hookOptions});

  useEffect(() => {
    // when component mounts, update the location bar with query info from storage.
    applyFilter(router, location, filters);
  }, []);

  const dashboardPassedProps: DashboardPassedProps<FetchParams> = {
    resetFilters,
    viewingAll,
    count: response != null ? response.count : null,
    params,
    refetch,
  };

  useModelChangeSubscription((message, utils) => {
    onModalChangeMessage?.(dashboardPassedProps)(message, utils);
  }, []);

  const [selectedDocs, setSelectedDocs] = useState<Array<ItemType>>([]);

  useEffect(() => {
    if (response == null) {
      setSelectedDocs([]);
      return;
    }
    setSelectedDocs((currentlySelectedDocs) =>
      response.results.filter((selectedDoc) =>
        currentlySelectedDocs.some((doc) => doc.id === selectedDoc.id)
      )
    );
  }, [response]);

  const isIndeterminateState = useMemo(
    () =>
      response != null
        ? selectedDocs.length !== response.results.length &&
          selectedDocs.length > 0
        : false,
    [selectedDocs, response]
  );
  const handleSelectedDocs = (isChecked: boolean) => {
    setSelectedDocs(isChecked && response != null ? response.results : []);
  };

  const addToSelection = (documentToAdd: ItemType) => {
    setSelectedDocs((currentlySelectedDocs) => {
      const docExists =
        currentlySelectedDocs.find(
          (selectedDoc) => selectedDoc.id === documentToAdd.id
        ) != null;

      if (docExists) {
        return currentlySelectedDocs;
      } else {
        return currentlySelectedDocs.concat([documentToAdd]);
      }
    });
  };

  const removeFromSelection = (documentToRemove: ItemType) => {
    setSelectedDocs((currentlySelectedDocs) =>
      currentlySelectedDocs.filter(
        (selectedDoc) => documentToRemove.id !== selectedDoc.id
      )
    );
  };

  return (
    <SPPage {...pageProps}>
      <div className="row">
        {sidebar != null ? (
          <BootstrapMinMediumMediaQuery>
            <StyledSidebarDIV>{sidebar}</StyledSidebarDIV>
          </BootstrapMinMediumMediaQuery>
        ) : null}
        <MainCol noSidebar={sidebar == null}>
          {filterbar != null ? filterbar(dashboardPassedProps) : null}
          <div className="d-flex justify-content-between">
            <div className="d-flex justify-content">
              {enableBulkSelect && response != null && response.count > 0 && (
                <CheckboxButton
                  isChecked={
                    selectedDocs.length === response.results.length ||
                    isIndeterminateState
                  }
                  onClick={(isChecked) => handleSelectedDocs(isChecked)}
                  useRealCheckbox={true}
                  isIndeterminate={isIndeterminateState}
                  className="mr-3 btn btn-sm btn-link"
                />
              )}
              {messages.counter != null && response != null ? (
                <DashboardItemsCounter
                  text={messages.counter(response.count)}
                  showSearchHelp={location.query.search != null}
                />
              ) : null}
              {enableBulkSelect && selectedDocs.length > 0 ? (
                <span className="my-2 small">
                  &nbsp;{gt('(%s selected)', selectedDocs.length)}
                </span>
              ) : null}
            </div>
            <div>
              {messages.topListActions != null && response != null
                ? messages.topListActions(selectedDocs, dashboardPassedProps)
                : null}
            </div>
          </div>
          {header != null && response != null
            ? header(dashboardPassedProps)
            : null}
          {response == null || (currentData == null && isFetching) ? (
            <DashboardLoadingContent />
          ) : response != null && response.count > 0 ? (
            enableNotifications && contentType ? (
              <DocumentsNotificationsProvider
                contentType={contentType}
                documentsIds={response.results.map((item) => item.id)}
              >
                {(notifications) =>
                  response.results.map((item) =>
                    renderItem(
                      item,
                      dashboardPassedProps,
                      selectedDocs.find(
                        (selectedDoc) => selectedDoc.id === item.id
                      ) != null,
                      addToSelection,
                      removeFromSelection,
                      item.url != null ? notifications[item.url] : []
                    )
                  )
                }
              </DocumentsNotificationsProvider>
            ) : (
              response.results.map((item) =>
                renderItem(
                  item,
                  dashboardPassedProps,
                  selectedDocs.find(
                    (selectedDoc) => selectedDoc.id === item.id
                  ) != null,
                  addToSelection,
                  removeFromSelection
                )
              )
            )
          ) : response != null && response.count == 0 ? (
            // not found
            messages.notFound(dashboardPassedProps, NoResultsMessage)
          ) : null}
          {response != null ? (
            <LinkPagination
              className="mt-3"
              page={params.page}
              response={convertListResponseToStoredResponse(
                response,
                params.page
              )}
            />
          ) : null}
          {footer != null && response != null
            ? footer(dashboardPassedProps)
            : null}
          {response != null &&
          response.count > 0 &&
          subscribeToPresence != null ? (
            <PresenceSubscriber
              contentType={subscribeToPresence.contentType}
              objectsIds={subscribeToPresence.objectsIds(response.results)}
              stealth={true}
            />
          ) : null}
        </MainCol>
      </div>
      <DashboardChatWidget />
    </SPPage>
  );
}
